import React, { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { deleteData, getData, postData, updateData } from "../../services/request"

const statusLabel = (status) => {
  const colors = {
    PENDING: 'bg-blue-100 text-blue-700',
    PRICED: 'bg-indigo-100 text-indigo-700',
    PAID: 'bg-green-100 text-green-700',
    PAYMENT_CONFIRMED: 'bg-emerald-100 text-emerald-700',
    DELIVERED: 'bg-purple-100 text-purple-700',
    RECEIVED: 'bg-teal-100 text-teal-700',
    CANCELLED: 'bg-red-100 text-red-700'
  }
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${colors[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>
}

const infoRow = (label, value) => (
  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 py-3 text-sm">
    <div className="col-span-1 font-medium text-gray-600">{label}</div>
    <div className="col-span-2 text-gray-900">{value || '—'}</div>
  </div>
)

const AdminRequestDetails = () => {
  const { postId } = useParams()
  const [request, setRequest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [actionError, setActionError] = useState("")
  const [actionMsg, setActionMsg] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [disputes, setDisputes] = useState([])
  const [loadingDisputes, setLoadingDisputes] = useState(true)

  const fetchRequest = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/posts/${postId}`
    const res = await getData(url, {}, {})
    setLoading(false)
    if(res.error == ""){
      if(res.result.status === 200){
        setRequest(res.result.data?.data || null)
      }else{
        setError(res.result.data?.message || 'Request not found')
      }
    }else{
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const fetchDisputes = async () => {
    setLoadingDisputes(true)
    try {
      const url = `${getAPIBaseURL()}/v1/disputes`
      const params = { limit: 200, skip: 0 }
      const res = await getData(url, {}, params)
      if (res.error == "") {
        if (res.result.status === 200) {
          const all = res.result.data?.data || []
          const filtered = all.filter((d) => {
            const dpId = d.postId?._id || d.postId
            return dpId && String(dpId) === String(postId)
          })
          setDisputes(filtered)
        } else {
          setDisputes([])
        }
      } else {
        setDisputes([])
      }
    } finally {
      setLoadingDisputes(false)
    }
  }

  useEffect(() => {
    fetchRequest()
    fetchDisputes()
  }, [postId])

  const cancelRequest = async () => {
    setSubmitting(true)
    setActionError("")
    setActionMsg("")
    const url = `${getAPIBaseURL()}/v1/posts/cancel`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('postId', postId)
    const res = await updateData(url, headers, params.toString())
    setSubmitting(false)
    if(res.error == ""){
      if(res.result.status === 200){
        setActionMsg('Request cancelled')
        fetchRequest()
      }else{
        setActionError(res.result.data?.message || 'Unable to cancel request')
      }
    }else{
      setActionError(res.result?.data?.message || 'Network error while cancelling request')
    }
  }

  const reactivateRequest = async () => {
    setSubmitting(true)
    setActionError("")
    setActionMsg("")
    const url = `${getAPIBaseURL()}/v1/posts/status`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('postId', postId)
    params.append('status', 'PENDING')
    const res = await postData(url, headers, params.toString())
    setSubmitting(false)
    if(res.error == ""){
      if(res.result.status === 200){
        setActionMsg('Request status updated to PENDING')
        fetchRequest()
      }else{
        setActionError(res.result.data?.message || 'Unable to reactivate request')
      }
    }else{
      setActionError(res.result?.data?.message || 'Network error while updating status')
    }
  }

  const deleteRequest = async () => {
    setSubmitting(true)
    setActionError("")
    setActionMsg("")
    const url = `${getAPIBaseURL()}/v1/posts`
    const res = await deleteData(url, {}, { postId })
    setSubmitting(false)
    if(res.error == ""){
      if(res.result.status === 200){
        setActionMsg('Request deleted')
        fetchRequest()
      }else{
        setActionError(res.result.data?.message || 'Unable to delete request')
      }
    }else{
      setActionError(res.result?.data?.message || 'Network error while deleting request')
    }
  }

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString() } catch { return iso }
  }

  return (
    <section className="mx-auto max-w-4xl space-y-4">
      <Link to="/admin/app/requests" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900">
        ← Back to requests
      </Link>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-5">
          <h1 className="text-2xl font-bold text-gray-900">Request Details</h1>
          <p className="mt-1 text-sm text-gray-600">
            Request ID:{" "}
            <span className="font-mono text-gray-900">
              {request?.requestId || postId}
            </span>
          </p>
        </div>

        <div className="px-6 py-6 space-y-6">
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">{error}</div>
          ) : !request ? (
            <div className="text-sm text-gray-500">Request not found.</div>
          ) : (
            <>
              {actionError ? <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">{actionError}</div> : null}
              {actionMsg ? <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-700">{actionMsg}</div> : null}

              <div className="rounded-lg border border-gray-100">
                {infoRow('Patient', `${request.userId?.name || 'Unknown'} (${request.userId?.email || '—'})`)}
                {infoRow('Status', statusLabel(request.status))}
                {infoRow('Message', request.message)}
                {infoRow('Created At', formatDate(request.createdAt))}
                {infoRow('Updated At', formatDate(request.updatedAt))}
                {infoRow('Selected Hospital', request.selectedHospital?.organization || 'Not selected')}
              </div>

              <div>
                <h2 className="mb-3 text-sm font-semibold text-gray-900">Prices from hospitals</h2>
                {Array.isArray(request.prices) && request.prices.length > 0 ? (
                  <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
                    {request.prices.map((price) => (
                      <li key={`${price.userId?._id || price.userId}-${price.price}`} className="flex items-center justify-between gap-4 p-3 sm:p-4">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">{price.hospitalName || price.userId?.organization || 'Hospital'}</p>
                          {price.comment ? <p className="mt-0.5 text-xs text-gray-500">{price.comment}</p> : null}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-gray-900">₦{Number(price.price).toLocaleString()}</span>
                          <span className="text-xs text-gray-500">{formatDate(price.timestamp)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No prices submitted</p>
                )}
              </div>

              <div>
                <h2 className="mb-3 text-sm font-semibold text-gray-900">Disputes for this request</h2>
                {loadingDisputes ? (
                  <p className="text-sm text-gray-500">Loading disputes...</p>
                ) : disputes.length === 0 ? (
                  <p className="text-sm text-gray-500">No disputes submitted for this request.</p>
                ) : (
                  <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
                    {disputes.map((dispute) => (
                      <li key={dispute._id} className="flex items-start justify-between gap-4 p-3 sm:p-4">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900">
                            {dispute.subject}
                          </p>
                          <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                            {dispute.message}
                          </p>
                          <p className="mt-1 text-xs text-gray-400">
                            By: {dispute.createdBy?.organization || dispute.createdBy?.name || 'Unknown'} (
                            {dispute.createdByRole}) • {formatDate(dispute.createdAt)}
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                              dispute.status === 'resolved'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}
                          >
                            {dispute.status?.toUpperCase() || 'OPEN'}
                          </span>
                          <div className="mt-2">
                            <Link
                              to={`/admin/app/disputes/${dispute._id}`}
                              className="text-xs font-medium text-gray-700 hover:underline"
                            >
                              View dispute
                            </Link>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex flex-wrap gap-3">
                {request.status !== 'CANCELLED' ? (
                  <button onClick={cancelRequest} disabled={submitting} className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70">
                    Deactivate (Cancel)
                  </button>
                ) : (
                  <button onClick={reactivateRequest} disabled={submitting} className="rounded-lg border border-blue-200 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-70">
                    Activate (Set Pending)
                  </button>
                )}
                <button onClick={deleteRequest} disabled={submitting} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-70">
                  Delete Request
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}

export default AdminRequestDetails


